import { useState, useEffect } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Search, Trash2, UserCheck, PlayCircle, Archive, FileWarning } from "lucide-react";
import api from "@/lib/api";

const inp = "w-full bg-muted/40 border border-border rounded-lg px-4 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50 font-mono";

const DEFAULT_PATTERNS = [
  "^TEST_",
  "^TEST ",
  "^Cuidado$",
  "^Dedup ",
  "^PATCH ",
  "^Push ",
  "^Zoho Cuidado$",
];

export default function MaintenanceSettings() {
  const [patterns, setPatterns] = useState(DEFAULT_PATTERNS.join("\n"));
  const [alsoZoho, setAlsoZoho] = useState(true);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // { matched, sample, patterns }
  const [confirmText, setConfirmText] = useState("");

  // Backfill estimate_prepared_by_id (08/02/2026)
  const [bfLoading, setBfLoading] = useState(false);
  const [bfPreview, setBfPreview] = useState(null);  // { stats, sample }
  const [bfExecuting, setBfExecuting] = useState(false);
  const [bfResult, setBfResult] = useState(null);    // { stats }

  // Legacy untreated leads counter (08/02/2026)
  const [legacyLoading, setLegacyLoading] = useState(true);
  const [legacy, setLegacy] = useState(null);   // {total, ever_touched, never_touched, sample, system_start_date}
  const [legacyClosing, setLegacyClosing] = useState(false);
  const [legacyClosedCount, setLegacyClosedCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/legacy-untreated").then((r) => {
      if (!cancelled) setLegacy(r.data);
    }).catch(() => {
      /* silent — user may not be admin */
    }).finally(() => {
      if (!cancelled) setLegacyLoading(false);
    });
    return () => { cancelled = true; };
  }, [legacyClosedCount]);

  const bulkCloseLegacy = async () => {
    if (!legacy || legacy.total_never_touched === 0) return;
    const msg = `Vai marcar ${legacy.total_never_touched} lead(s) legado como "Não qualificado" (motivo: legacy_pre_arranque). Só afecta leads NUNCA tocados. Continuar?`;
    if (!window.confirm(msg)) return;
    setLegacyClosing(true);
    try {
      const r = await api.post("/admin/legacy-untreated/bulk-close", {
        dry_run: false,
        reason: "legacy_pre_arranque",
      });
      toast.success(`${r.data.closed} lead(s) legado marcados como não qualificado.`);
      setLegacyClosedCount(r.data.closed);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao fechar leads legado");
    } finally { setLegacyClosing(false); }
  };

  const parsePatterns = () => patterns.split("\n").map(s => s.trim()).filter(Boolean);

  const runPreview = async () => {
    const list = parsePatterns();
    if (!list.length) { toast.error("Indica pelo menos um padrão regex"); return; }
    setLoading(true);
    try {
      const r = await api.post("/admin/purge-test-leads", { patterns: list, dry_run: true, also_zoho: alsoZoho });
      setPreview(r.data);
      toast.success(`${r.data.matched} lead(s) correspondem aos padrões`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no preview");
    } finally { setLoading(false); }
  };

  const executePurge = async () => {
    if (!preview) return;
    if (confirmText.trim() !== "ELIMINAR") { toast.error("Escreve ELIMINAR para confirmar"); return; }
    const msg = `Vais eliminar ${preview.matched} lead(s)` + (alsoZoho ? " (cascade Zoho CRM incluído)" : " (apenas local)") + ". Esta ação é IRREVERSÍVEL. Continuar?";
    if (!window.confirm(msg)) return;
    setLoading(true);
    try {
      const r = await api.post("/admin/purge-test-leads", {
        patterns: parsePatterns(),
        dry_run: false,
        also_zoho: alsoZoho,
        confirm: "ELIMINAR",
      });
      toast.success(`${r.data.deleted} lead(s) eliminado(s). Zoho OK: ${r.data.zoho_deleted}, falhou: ${r.data.zoho_failed}`);
      setPreview(null);
      setConfirmText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao eliminar");
    } finally { setLoading(false); }
  };

  // ---- Backfill estimate_prepared_by_id ----
  const runBackfillPreview = async () => {
    setBfLoading(true);
    setBfResult(null);
    try {
      const r = await api.post("/admin/estimate-preparer/backfill",
                                 { dry_run: true });
      setBfPreview(r.data);
      toast.success(`${r.data.stats.would_update} lead(s) receberão preparador; ${r.data.stats.no_signal} sem sinal.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no preview do backfill");
    } finally { setBfLoading(false); }
  };

  const executeBackfill = async () => {
    if (!bfPreview) return;
    const msg = `Vai preencher \`estimate_prepared_by_id\` em ${bfPreview.stats.would_update} lead(s). Continuar?`;
    if (!window.confirm(msg)) return;
    setBfExecuting(true);
    try {
      const r = await api.post("/admin/estimate-preparer/backfill",
                                 { dry_run: false });
      setBfResult(r.data);
      setBfPreview(null);
      toast.success(`${r.data.stats.updated} lead(s) actualizados. ${r.data.stats.no_signal} sem sinal.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao executar backfill");
    } finally { setBfExecuting(false); }
  };

  return (
    <div className="p-8 lg:p-12 max-w-4xl mx-auto space-y-6" data-testid="maintenance-root">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Administração</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Manutenção</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Ferramentas de limpeza operacional. Todas as ações são registadas nos logs do backend.</p>
      </div>

      {/* ---------- Legado por Fechar (visibility widget) ---------- */}
      <div className="bg-card rounded-2xl border border-border p-8 space-y-6" data-testid="legacy-counter-card">
        <div className="flex items-start gap-3 pb-4 border-b border-border">
          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--warning-bg))] flex items-center justify-center flex-shrink-0">
            <Archive className="w-5 h-5 text-[hsl(var(--warning))]" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-2xl">Legado por fechar</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Leads criados <b>antes</b> do arranque do sistema
              {legacy?.system_start_date && (
                <> (<span className="font-mono text-foreground">{legacy.system_start_date}</span>)</>
              )} que continuam em <code className="bg-muted/40 px-1 rounded">qualificacao_inicial</code>.
              Estes já <b>não contaminam</b> a fila SLA nem o topo do listing (fix 08/02) — este contador dá-lhe visibilidade para tratar em batch quando for oportuno.
            </p>
          </div>
        </div>

        {legacyLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            A carregar contagem…
          </div>
        ) : !legacy ? (
          <div className="text-sm text-muted-foreground py-4" data-testid="legacy-no-access">Sem permissão para ver o contador legado.</div>
        ) : legacy.total === 0 ? (
          <div className="rounded-xl bg-[hsl(var(--success-bg))] border border-[#059669] p-5" data-testid="legacy-empty">
            <div className="text-sm text-[#065F46] font-semibold">Nenhum lead legado por fechar</div>
            <div className="text-xs text-[#047857] mt-1">Todos os leads em <code>qualificacao_inicial</code> foram criados a partir do arranque do sistema. 🎉</div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-muted/40 border border-border rounded-xl p-4" data-testid="legacy-total">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Total legado</div>
                <div className="font-ui text-3xl tabular-nums text-foreground">{legacy.total}</div>
                <div className="text-xs text-muted-foreground mt-1">em qualificacao_inicial</div>
              </div>
              <div className="bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))] rounded-xl p-4" data-testid="legacy-never-touched">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--warning))] font-semibold mb-1">Nunca tocados</div>
                <div className="font-ui text-3xl tabular-nums text-[#78350F]">{legacy.total_never_touched}</div>
                <div className="text-xs text-[hsl(var(--warning))] mt-1">candidatos a fechar em massa</div>
              </div>
              <div className="bg-muted/40 border border-border rounded-xl p-4" data-testid="legacy-touched">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Já tocados</div>
                <div className="font-ui text-3xl tabular-nums text-foreground">{legacy.total_ever_touched}</div>
                <div className="text-xs text-muted-foreground mt-1">revisão caso-a-caso</div>
              </div>
            </div>

            {legacy.sample?.length > 0 && (
              <div>
                <div className="text-xs text-muted-foreground font-semibold mb-2">
                  Amostra dos mais antigos ({legacy.sample.length} de {legacy.total})
                </div>
                <div className="max-h-72 overflow-y-auto bg-muted/40 rounded-xl border border-border text-xs" data-testid="legacy-sample-list">
                  <table className="w-full">
                    <thead className="bg-card sticky top-0 border-b border-border">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Data</th>
                        <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Cliente</th>
                        <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Distrito</th>
                        <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Tocado?</th>
                        <th className="text-right px-3 py-2 font-semibold text-muted-foreground">Lead</th>
                      </tr>
                    </thead>
                    <tbody>
                      {legacy.sample.map((s) => (
                        <tr key={s.id} className="border-t border-border hover:bg-card">
                          <td className="px-3 py-1.5 font-mono text-muted-foreground tabular-nums">{(s.created_at || "").slice(0, 10)}</td>
                          <td className="px-3 py-1.5 text-foreground">{s.name}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{s.district || "—"}</td>
                          <td className="px-3 py-1.5">
                            {s.touched
                              ? <span className="inline-flex items-center gap-1 text-[#059669] font-semibold"><FileWarning className="w-3 h-3" /> Sim</span>
                              : <span className="text-muted-foreground">Não</span>}
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            <a href={`/leads/${s.id}`} target="_blank" rel="noreferrer" className="text-foreground hover:text-foreground font-semibold underline">Abrir</a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {legacy.total_never_touched > 0 && (
              <div className="rounded-xl bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))] p-5" data-testid="legacy-bulk-close-panel">
                <div className="text-sm text-[#78350F] font-semibold mb-1">Fechar em massa os "nunca tocados"</div>
                <div className="text-xs text-[#92400E] mb-3">
                  Marca {legacy.total_never_touched} lead(s) como <code className="bg-card px-1 rounded">nao_qualificado</code> com motivo <code className="bg-card px-1 rounded">legacy_pre_arranque</code>. Só afecta leads legado que <b>nunca</b> foram tocados no fluxo actual — os já tocados ficam intactos para revisão manual.
                </div>
                <button
                  onClick={bulkCloseLegacy}
                  disabled={legacyClosing}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#B45309] text-primary-foreground text-sm font-medium hover:bg-[#78350F] disabled:opacity-50"
                  data-testid="legacy-bulk-close-btn"
                >
                  {legacyClosing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                  Fechar {legacy.total_never_touched} leads legado
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="bg-card rounded-2xl border border-border p-8 space-y-6" data-testid="purge-card">
        <div className="flex items-start gap-3 pb-4 border-b border-border">
          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--danger-bg))] flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-5 h-5 text-[hsl(var(--danger))]" />
          </div>
          <div>
            <h2 className="font-display text-2xl">Purga de leads de teste</h2>
            <p className="text-sm text-muted-foreground mt-1">Encontra leads cujo nome de contacto começa por um dos padrões regex abaixo e elimina-os em cascata (proposal_versions, sync_logs, email_logs). Opcionalmente também apaga no Zoho CRM.</p>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold block mb-2">Padrões regex (1 por linha)</label>
          <textarea
            className={inp + " min-h-[140px] resize-y"}
            value={patterns}
            onChange={e => { setPatterns(e.target.value); setPreview(null); setConfirmText(""); }}
            data-testid="purge-patterns-input"
          />
          <p className="text-xs text-muted-foreground mt-2">Cada padrão é aplicado ao campo <code className="bg-muted/40 px-1 rounded">qualification.contact.name</code>. Ex: <code className="bg-muted/40 px-1 rounded">^TEST_</code> apanha "TEST_QT_123".</p>
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={alsoZoho}
              onChange={e => setAlsoZoho(e.target.checked)}
              data-testid="purge-also-zoho"
            />
            <span>Cascade também no Zoho CRM (recomendado)</span>
          </label>
        </div>

        <div className="flex gap-3">
          <button
            onClick={runPreview}
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-[#1E293B] disabled:opacity-50"
            data-testid="purge-preview-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Pré-visualizar (dry-run)
          </button>
        </div>

        {preview && (
          <div className="border-2 border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger-bg))] rounded-xl p-5 space-y-4" data-testid="purge-preview">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-[hsl(var(--danger))] flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-[#991B1B]">{preview.matched} lead(s) serão eliminados</div>
                <div className="text-xs text-[#7F1D1D] mt-1">Amostra dos primeiros {Math.min(preview.sample?.length || 0, 200)} abaixo. Confirma escrevendo <code className="bg-card px-1 rounded">ELIMINAR</code>.</div>
              </div>
            </div>

            {preview.sample?.length > 0 && (
              <div className="max-h-64 overflow-y-auto bg-card rounded-lg border border-[hsl(var(--danger))]/30 text-xs" data-testid="purge-preview-list">
                <table className="w-full">
                  <thead className="bg-[hsl(var(--danger-bg))] sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-[#7F1D1D]">Nome</th>
                      <th className="text-left px-3 py-2 font-semibold text-[#7F1D1D]">Telefone</th>
                      <th className="text-left px-3 py-2 font-semibold text-[#7F1D1D]">Criado</th>
                      <th className="text-left px-3 py-2 font-semibold text-[#7F1D1D]">Zoho</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample.map(s => (
                      <tr key={s.id} className="border-t border-[#FEE2E2]">
                        <td className="px-3 py-1.5 font-mono">{s.name || "—"}</td>
                        <td className="px-3 py-1.5 font-mono">{s.phone || "—"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{(s.created_at || "").substring(0,10)}</td>
                        <td className="px-3 py-1.5">{s.has_zoho_id ? "✓" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <input
                type="text"
                placeholder='Escreve "ELIMINAR" para confirmar'
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                className="flex-1 bg-card border border-[hsl(var(--danger))]/30 rounded-lg px-4 py-2.5 text-sm font-mono"
                data-testid="purge-confirm-input"
              />
              <button
                onClick={executePurge}
                disabled={loading || confirmText.trim() !== "ELIMINAR" || preview.matched === 0}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[hsl(var(--danger))] text-white text-sm font-medium hover:bg-[hsl(var(--danger))]/85 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="purge-execute-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Eliminar {preview.matched} lead(s)
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ---------- Backfill estimate_prepared_by_id ---------- */}
      <div className="bg-card rounded-2xl border border-border p-8 space-y-6" data-testid="backfill-preparer-card">
        <div className="flex items-start gap-3 pb-4 border-b border-border">
          <div className="w-10 h-10 rounded-lg bg-background flex items-center justify-center flex-shrink-0">
            <UserCheck className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-display text-2xl">Backfill preparador da estimativa</h2>
            <p className="text-sm text-muted-foreground mt-1">Preenche <code className="bg-muted/40 px-1 rounded">estimate_prepared_by_id</code> em leads legado onde o campo ainda está vazio. Nesses leads, a capa do PDF caía no fallback ("Consultor de Cuidados") — depois do backfill passa a mostrar quem realmente preparou a estimativa.</p>
            <p className="text-xs text-muted-foreground mt-2">Sinal usado (por ordem): 1) primeiro <code className="bg-muted/40 px-1 rounded">estimate_history[].sent_by_user_id</code> 2) <code className="bg-muted/40 px-1 rounded">first_contact_by_id</code>. Idempotente — leads que já têm preparador não são tocados.</p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={runBackfillPreview}
            disabled={bfLoading || bfExecuting}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-[#1E293B] disabled:opacity-50"
            data-testid="backfill-preview-btn"
          >
            {bfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Pré-visualizar (dry-run)
          </button>
        </div>

        {bfPreview && (
          <div className="border-2 border-[#93C5FD] bg-background rounded-xl p-5 space-y-4" data-testid="backfill-preview">
            <div>
              <div className="font-semibold text-primary">Resultado do dry-run</div>
              <div className="text-xs text-[#1E40AF] mt-1 tabular-nums">
                Analisados: <b>{bfPreview.stats.seen}</b> · A actualizar: <b>{bfPreview.stats.would_update}</b> · Sem sinal: <b>{bfPreview.stats.no_signal}</b> · User inactivo: <b>{bfPreview.stats.user_inactive}</b>
              </div>
            </div>

            {bfPreview.sample?.length > 0 && (
              <div className="max-h-64 overflow-y-auto bg-card rounded-lg border border-[#BFDBFE] text-xs" data-testid="backfill-preview-list">
                <table className="w-full">
                  <thead className="bg-background sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-[#1E40AF]">Cliente</th>
                      <th className="text-left px-3 py-2 font-semibold text-[#1E40AF]">Preparador</th>
                      <th className="text-left px-3 py-2 font-semibold text-[#1E40AF]">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bfPreview.sample.map(s => (
                      <tr key={s.lead_id} className="border-t border-[#DBEAFE]">
                        <td className="px-3 py-1.5">{s.cliente || "—"}</td>
                        <td className="px-3 py-1.5 font-semibold">{s.preparer_name}</td>
                        <td className="px-3 py-1.5 text-muted-foreground font-mono">{s.preparer_email || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="text-[11px] text-primary px-3 py-2 border-t border-[#DBEAFE]">Amostra dos primeiros 20 · execução real actualiza todos os {bfPreview.stats.would_update}</div>
              </div>
            )}

            {bfPreview.stats.would_update > 0 && (
              <button
                onClick={executeBackfill}
                disabled={bfExecuting}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                data-testid="backfill-execute-btn"
              >
                {bfExecuting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                Executar backfill em {bfPreview.stats.would_update} lead(s)
              </button>
            )}
          </div>
        )}

        {bfResult && (
          <div className="border border-[#059669] bg-[hsl(var(--success-bg))] rounded-xl p-5" data-testid="backfill-result">
            <div className="font-semibold text-[#065F46]">Backfill concluído</div>
            <div className="text-xs text-[#047857] mt-1 tabular-nums">
              Actualizados: <b>{bfResult.stats.updated}</b> · Sem sinal: <b>{bfResult.stats.no_signal}</b> · User inactivo: <b>{bfResult.stats.user_inactive}</b>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
